import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { environment } from '../environments/environment';
import * as MixPanel from 'mixpanel-browser';

export const ANALYTICSSERVICE_TOKEN = new InjectionToken<IAnalyticsService[]>('AnalyticsService');

export interface IAnalyticsService {
    /**
     * Initialize the service.
     *
     * @memberof IAnalyticsService
     */
    init(): void;

    /**
     * Starts tracking actions as related to the user.
     *
     * @param {string} userToken
     * @memberof IAnalyticsService
     */
    identify(
        userToken: string): void;

    /**
     * Pushs new event to the provider.
     *
     * @param {string} majorSection The name of the major section of the application.
     * @param {string} pageOrSubSection The name of page or sub-section of that major section.
     * @param {string} actionName The action being performed.
     * @param {string} description A short description statement about why that event is happening.
     * @param {string} nextPath The path to where we are going next (passing null is allowed).
     * @param {any} [additionalDetails={}] Additional metadata to link to the event.
     * @memberof IAnalyticsService
     */
    track(
        majorSection: string,
        pageOrSubSection: string,
        actionName: string,
        description: string,
        nextPath: string,
        additionalDetails: any): void;
}

@Injectable({
    providedIn: 'root'
})
export class MixpanelService implements IAnalyticsService {
    private readonly mixpanel: typeof MixPanel;

    constructor() {
        this.mixpanel = MixPanel;
    }

    public init(): void {
        try {
            this.mixpanel.init(
                environment.MixPanelKey,
                {
                    debug: !environment.production,
                    ignore_dnt: true
                });
        } catch (error) {
            console.error(error);
        }
    }

    public identify(
        userToken: string): void {
        try {
            if (userToken !== '' && userToken !== undefined && userToken !== null) {
                this.mixpanel.identify(userToken);
            }
        } catch (error) {
            console.error(error);
        }
    }

    public track(
        majorSection: string,
        pageOrSubSection: string,
        actionName: string,
        description: string,
        nextPath: string = '',
        additionalDetails: any = {}): void {
        try {
            this.mixpanel.track(
                `${pageOrSubSection} - ${actionName}`,
                {
                    ...additionalDetails,
                    'Section': majorSection,
                    'SubSection': pageOrSubSection,
                    'Action': actionName,
                    'Description': description,
                    'NextPath': nextPath
                });
        } catch (error) {
            console.error(error);
        }
    }
}

@Injectable({
    providedIn: 'root'
})
export class AnalyticsService {
    constructor(@Optional() @Inject(ANALYTICSSERVICE_TOKEN) private readonly analyticsServices?: IAnalyticsService[]) { }

    /**
     * Initialize all services.
     *
     * @memberof AnalyticsService
     */
    init(): void {
        this.analyticsServices?.forEach(service => {
            try {
                service.init();
            } catch (error) {
                console.error(error);
            }
        });
    }

    /**
     * Starts tracking actions as related to the user.
     *
     * @param {string} userToken
     * @memberof AnalyticsService
     */
    identify(userToken: string): void {
        this.analyticsServices?.forEach(service => {
            try {
                service.identify(userToken);
            } catch (error) {
                console.error(error);
            }
        });
    }

    /**
     * Pushs new event to the provider.
     *
     * @param {string} majorSection The name of the major section of the application.
     * @param {string} pageOrSubSection The name of page or sub-section of that major section.
     * @param {string} actionName The action being performed.
     * @param {string} description A short description statement about why that event is happening.
     * @param {string} nextPath The path to where we are going next (passing null is allowed).
     * @param {any} [additionalDetails={}] Additional metadata to link to the event.
     * @memberof IAnalyticsService
     */
    track(
        majorSection: string,
        pageOrSubSection: string,
        actionName: string,
        description: string,
        nextPath: string,
        additionalDetails: any): void {
        this.analyticsServices?.forEach(service => {
            try {
                service.track(
                    majorSection,
                    pageOrSubSection,
                    actionName,
                    description,
                    nextPath,
                    {
                        ...additionalDetails,
                        'YardiKey': localStorage.getItem('YardiKey'),
                        'AddAdditionalApplicantKey': localStorage.getItem('AddAdditionalApplicantKey'),
                        'RentalApplicationKey': localStorage.getItem('RentalApplicationKey'),
                        'RentalApplicantKey': localStorage.getItem('RentalApplicantKey')
                    });
            } catch (error) {
                console.error(error);
            }
        });
    }
}